/**
 * Client-side wrapper for `/viewer-api/path/*`.
 * Shape mirrors server-side src/pathReports.ts.
 */
import { auth } from "@viewer/auth/store";

export type PathStatus = "draft" | "finalized" | "amended";

export interface PathAnnotationWire {
  id?: string;
  kind: "rect" | "ellipse" | "polygon" | "freehand" | "point" | "ruler" | "text" | "snapshot";
  label?: string | null;
  color?: string | null;
  source?: "human" | "ai";
  instance_uid?: string | null;
  geometry: any;
}

export interface PathReportWire {
  id?: string;
  studyUid: string;
  userId: string;
  userName?: string | null;
  status: PathStatus;
  body: Record<string, any>;
  finalizedAt?: string | null;
  finalizedBy?: string | null;
  annotations: PathAnnotationWire[];
  seededFrom?: string | null;
}

function authHeaders(): Record<string, string> {
  const a = auth();
  return a?.accessToken ? { Authorization: `Bearer ${a.accessToken}` } : {};
}

export async function fetchReport(studyUid: string): Promise<PathReportWire | null> {
  const r = await fetch(`/viewer-api/path/reports/${encodeURIComponent(studyUid)}`, {
    headers: { Accept: "application/json", ...authHeaders() },
  });
  if (!r.ok) return null;
  return r.json();
}

/** Senior-pathologist override (Phase J). When the caller is not
 *  the case's assignee, the server requires `override_reason`
 *  (≥10 chars) on every write — save / finalize / amend. The
 *  helpers below accept it as an optional argument; the
 *  PathologyShell catches `OverrideRequiredError` thrown when the
 *  server returns 400 / `override_reason_required` and prompts the
 *  reader for a justification before retrying. */
export class OverrideRequiredError extends Error {
  assignee: string | null;
  constructor(message: string, assignee: string | null) {
    super(message);
    this.name = "OverrideRequiredError";
    this.assignee = assignee;
  }
}
export class NotAssignedError extends Error {
  constructor(message: string) {
    super(message);
    this.name = "NotAssignedError";
  }
}

async function handleWriteResponse(r: Response, label: string): Promise<PathReportWire> {
  if (r.ok) return r.json();
  const text = await r.text();
  let parsed: any = {};
  try { parsed = JSON.parse(text); } catch { /* keep raw text */ }
  if (r.status === 400 && parsed.error === "override_reason_required") {
    throw new OverrideRequiredError(parsed.message ?? "override reason required", parsed.assignee ?? null);
  }
  if (r.status === 403 && parsed.error === "not_assigned") {
    throw new NotAssignedError(parsed.message ?? "not assigned to this case");
  }
  throw new Error(`${label} failed ${r.status} — ${text}`);
}

export async function saveReport(
  studyUid: string,
  payload: { body: Record<string, any>; annotations: PathAnnotationWire[] },
  override_reason?: string,
): Promise<PathReportWire> {
  const r = await fetch(`/viewer-api/path/reports/${encodeURIComponent(studyUid)}`, {
    method: "PUT",
    headers: { "content-type": "application/json", Accept: "application/json", ...authHeaders() },
    body: JSON.stringify(override_reason ? { ...payload, override_reason } : payload),
  });
  return handleWriteResponse(r, "save");
}

export async function finalizeReport(studyUid: string, override_reason?: string): Promise<PathReportWire> {
  const r = await fetch(`/viewer-api/path/reports/${encodeURIComponent(studyUid)}/finalize`, {
    method: "POST",
    headers: { "content-type": "application/json", Accept: "application/json", ...authHeaders() },
    body: override_reason ? JSON.stringify({ override_reason }) : "{}",
  });
  return handleWriteResponse(r, "finalize");
}

export async function amendReport(studyUid: string, override_reason?: string): Promise<PathReportWire> {
  const r = await fetch(`/viewer-api/path/reports/${encodeURIComponent(studyUid)}/amend`, {
    method: "POST",
    headers: { "content-type": "application/json", Accept: "application/json", ...authHeaders() },
    body: override_reason ? JSON.stringify({ override_reason }) : "{}",
  });
  return handleWriteResponse(r, "amend");
}
